/**
 * WHERE EACH PLACE IS, as the screens link to each other, so a link and the route it lands on are
 * written once. `App.tsx` routes each path through `Entry`: Home at `/`, Schedule at `/schedule`
 * (on one clean at `/schedule?visit=`, `scheduleVisitPath`), Payments at `/payments`, Messages at
 * `/messages`, Feedback on one visit at `/feedback/:visitId`, one visit's report at
 * `/visits/:visitId` (`visitPath`, home #21), an invoice's own page at `/invoices/:invoiceId`
 * (`invoicePath`), the page Stripe sends her back to at `/payments/return`, and Account at
 * `/account` with its four pages.
 * Any other path lands on `/`. Sign-in is at `/sign-in`, which carries the page a signed-out
 * visitor came for as `next`, a path of these alone (`lib/cameFor.ts`).
 *
 * Nothing a person typed is ever in a path or a query, so none of it lands in a log or in the
 * browser's history: only ids ops minted, a face, which is one of four fixed words, a way to pay,
 * one of three, and the path of the page she came for, which is made of the same.
 */
import type { Face } from './words.ts';

export const HOME_PATH = '/';
export const SCHEDULE_PATH = '/schedule';

/** The query word Schedule reads the clean a link leads to from (`scheduleVisitPath`). */
export const VISIT_QUERY = 'visit';

/**
 * Schedule on one clean, where "See this clean" under a copy of the office's text leads (slice M1):
 * the clean's card on the List, or the month on its day. The id is ops's, encoded as the query's one
 * value, so whatever it holds names one visit and never another word of the query.
 */
export function scheduleVisitPath(visitId: string): string {
  return `${SCHEDULE_PATH}?${VISIT_QUERY}=${encodeURIComponent(visitId)}`;
}
export const PAYMENTS_PATH = '/payments';
export const MESSAGES_PATH = '/messages';

/**
 * Account, the page about her that the header's Account button leads to (slice A1), and its four
 * pages: How you pay, a client's alone, People and sign-in, Your agreement (round five, slice C2),
 * which Account's regular cleans lead to, and What we send you (ops's section 11.3, P114), offered
 * only once ops has pushed her choices.
 */
export const ACCOUNT_PATH = '/account';
export const ACCOUNT_PAYING_PATH = '/account/paying';
export const ACCOUNT_PEOPLE_PATH = '/account/people';
export const ACCOUNT_AGREEMENT_PATH = '/account/agreement';
export const ACCOUNT_SENDING_PATH = '/account/sending';

/** Whether a path is Account or one of its pages, where the header's Account button is current. */
export const onAccount = (path: string): boolean =>
  path === ACCOUNT_PATH || path.startsWith(`${ACCOUNT_PATH}/`);

/**
 * The page Stripe sends her back to, which the Worker writes as `return_url` from its own origin
 * (`api/src/seam/requests.ts`, `RETURN_PATH`), with nothing of hers in it: ops adds only `page`,
 * `saves` and `not-paid` (the seam, l.677-687 at b727ec1).
 */
export const PAYMENT_RETURN_PATH = '/payments/return';

/** Messages with the composer open, where "Ask us for something" leads. */
export const WRITE_PATH = `${MESSAGES_PATH}?write=1`;

/** The route Feedback is drawn at, for `App.tsx`. */
export const FEEDBACK_ROUTE = '/feedback/:visitId';

/** Feedback on one visit, with a face already chosen when it was tapped on the Today card. */
export function feedbackPath(visitId: string, face?: Face): string {
  const path = `/feedback/${encodeURIComponent(visitId)}`;
  return face === undefined ? path : `${path}?face=${face}`;
}

/** The route one visit's report is drawn at, for `App.tsx` (home #21). */
export const VISIT_ROUTE = '/visits/:visitId';

/**
 * One visit's report, where the visit-done email's "See the report and photos" leads once home says
 * the page is on production (home #21, ops #86). The id is ops's, encoded as the path's last part,
 * so whatever it holds names one visit and never another path.
 */
export function visitPath(visitId: string): string {
  return `/visits/${encodeURIComponent(visitId)}`;
}

/** The route an invoice's own page is drawn at, for `App.tsx`. */
export const INVOICE_ROUTE = '/invoices/:invoiceId';

/**
 * An invoice's own page, where Pay on Payments leads. The id is ops's, encoded as the path's last
 * part, so whatever it holds names one invoice and never another path.
 */
export function invoicePath(invoiceId: string): string {
  return `/invoices/${encodeURIComponent(invoiceId)}`;
}

/**
 * The query words of the links ops puts in front of her that lead to an invoice's own page (the
 * owner's decision of 2026-10-19, ops #76; `docs/seam.md` P107): `pay`, the way to pay it opens on,
 * `bank`, `card` or `zelle`; and `document`, the id of one of its PDFs, which the page asks for on
 * arrival. Ops writes these links; home only reads them (`screens/Invoice.tsx`).
 */
export const PAY_QUERY = 'pay';
export const DOCUMENT_QUERY = 'document';

/**
 * The door's Sign in, the two steps of the email address and the code. A signed-out visitor to a
 * deeper page is sent here with that page as the one query word, `next` (`lib/cameFor.ts`), and
 * brought back to it once her code has gone in.
 */
export const SIGN_IN_PATH = '/sign-in';
export const NEXT_QUERY = 'next';
